import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Pagination from '../../components/common/Pagination'
import api from '../../utils/api'
import { eventAPI, supplierAPI, setupAPI, associationAPI } from '../../utils/apiHelpers'

const IntegrationsPage = ({ eventsData, setCurrentIntegration, onNavigateToMapping }) => {
    const { token } = useAuth();
    const [integrations, setIntegrations] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [editingIntegration, setEditingIntegration] = useState(null);
    const [formData, setFormData] = useState({ name: '', logo_url: '', supplier_id: null, system_id: null, documentation_links: [], active: true });
    const [newLink, setNewLink] = useState({ title: '', url: '' });
    const [linkErrors, setLinkErrors] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
    const [selectedIntegrationForEvents, setSelectedIntegrationForEvents] = useState(null);
    const [integrationEvents, setIntegrationEvents] = useState({});
    const [logoValid, setLogoValid] = useState(null);
    const [localEventsData, setLocalEventsData] = useState([]);
    const [eventSearchTerm, setEventSearchTerm] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});

    // Estados de paginação
    const [page, setPage] = useState(1);
    const [itemsPerPage] = useState(5);

    // Estados para expansão de acordeão por fornecedor
    const [expandedIntegrationSuppliers, setExpandedIntegrationSuppliers] = useState({});

    // Group integrations by supplier
    const groupIntegrationsBySupplier = (integrations) => {
        return integrations.reduce((acc, integration) => {
            const supplier = suppliers.find(s => s.id === integration.supplier_id);
            const key = supplier ? supplier.id : 'no-supplier';
            const supplierName = supplier ? supplier.name : 'Fornecedor não especificado';
            const logoUrl = supplier ? supplier.logo_url : null;

            if (!acc[key]) {
                acc[key] = { name: supplierName, logo_url: logoUrl, integrations: [] };
            }
            acc[key].integrations.push(integration);
            return acc;
        }, {});
    };

    // Toggle supplier expansion in integrations
    const toggleIntegrationSupplierExpansion = (supplierId) => {
        setExpandedIntegrationSuppliers(prev => ({
            ...prev,
            [supplierId]: !prev[supplierId]
        }));
    };

    // Estados para seletor de conector
    const [connectors, setConnectors] = useState([]);
    const [connectorsLoading, setConnectorsLoading] = useState(false);
    const [connectorSearch, setConnectorSearch] = useState('');
    const [connectorDropdownOpen, setConnectorDropdownOpen] = useState(false);
    const connectorDropdownRef = React.useRef(null);

    // Estados para modal de configurações/parâmetros
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [selectedIntegrationForConfig, setSelectedIntegrationForConfig] = useState(null);
    const [configParameters, setConfigParameters] = useState([]);
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);

    // Estado para URL do webhook padrão
    const [defaultWebhookUrl, setDefaultWebhookUrl] = useState(null);
    const [webhookLoading, setWebhookLoading] = useState(true);
    const [webhookAccordionOpen, setWebhookAccordionOpen] = useState(false);

    // Click-outside handler para dropdown de conectores
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (connectorDropdownRef.current && !connectorDropdownRef.current.contains(event.target)) {
                setConnectorDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load default webhook URL
    useEffect(() => {
        const loadWebhookUrl = async () => {
            try {
                const response = await api.get('/setup/webhook-url');
                setDefaultWebhookUrl(response.data.webhook_url);
            } catch (error) {
                console.error('Erro ao carregar URL do webhook:', error);
            } finally {
                setWebhookLoading(false);
            }
        };
        loadWebhookUrl();
    }, []);

    // Tipos de parâmetros disponíveis
    const parameterTypes = [
        { value: 'string', label: 'Texto' },
        { value: 'integer', label: 'Inteiro' },
        { value: 'float', label: 'Decimal' },
        { value: 'boolean', label: 'Booleano' },
        { value: 'date', label: 'Data' },
        { value: 'datetime', label: 'Data/Hora' },
        { value: 'json', label: 'JSON' },
    ];

    // Filter integrations based on search term
    const filteredIntegrations = integrations.filter(integration => {
        const searchLower = searchTerm.toLowerCase();
        const supplier = suppliers.find(s => s.id === integration.supplier_id);
        const system = supplier?.systems?.find(sys => sys.id === integration.system_id);
        const systemType = integration.system?.system_type;

        return (
            integration.id.toLowerCase().includes(searchLower) ||
            integration.name.toLowerCase().includes(searchLower) ||
            supplier?.name.toLowerCase().includes(searchLower) ||
            (system?.name || system?.description || '').toLowerCase().includes(searchLower) ||
            systemType?.name.toLowerCase().includes(searchLower)
        );
    });

    // Group integrations by supplier first
    const groupedIntegrationsBySupplier = groupIntegrationsBySupplier(filteredIntegrations);
    const supplierKeys = Object.keys(groupedIntegrationsBySupplier).sort((a, b) => {
        const nameA = groupedIntegrationsBySupplier[a].name.toLowerCase();
        const nameB = groupedIntegrationsBySupplier[b].name.toLowerCase();
        return nameA.localeCompare(nameB);
    });

    // Pagination logic - paginate by supplier, not by integration
    const suppliersPerPage = 5;
    const totalSupplierPages = Math.ceil(supplierKeys.length / suppliersPerPage);
    const supplierStartIndex = (page - 1) * suppliersPerPage;
    const supplierEndIndex = supplierStartIndex + suppliersPerPage;
    const paginatedSupplierKeys = supplierKeys.slice(supplierStartIndex, supplierEndIndex);

    // Reset to page 1 when search term changes
    React.useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const validateLogoUrl = (url) => {
        if (!url) {
            setLogoValid(null);
            return;
        }

        setLogoValid(null); // Loading state

        // Criar um timeout para evitar carregamento infinito
        const timeoutId = setTimeout(() => {

            setLogoValid(false);
        }, 3000);

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            clearTimeout(timeoutId);

            setLogoValid(true);
        };

        img.onerror = () => {
            clearTimeout(timeoutId);

            setLogoValid(false);
        };

        // Definir src por último para evitar race conditions
        img.src = url;
    };

    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    useEffect(() => {
        if (token) {
            fetchIntegrations();
            loadGlobalEvents(); // Carregar todos os eventos globais disponíveis
            loadSuppliers(); // Carregar todos os fornecedores
            loadConnectors(); // Carregar arquivos de conectores
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadGlobalEvents = async () => {
        try {
            const response = await api.get('/events', {
                params: { page: 1, page_size: 100 }
            });
            const allEvents = response.data.items || [];
            setLocalEventsData(allEvents);
        } catch (error) {

        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const loadSuppliers = async () => {
        try {
            const response = await api.get('/suppliers', {
                params: { page: 1, page_size: 100 }
            });
            setSuppliers(response.data.items || []);
        } catch (error) {

        } finally {
        }
    };

    const loadConnectors = async () => {
        setConnectorsLoading(true);
        try {
            const response = await api.get('/connectors');
            setConnectors(response.data || []);
        } catch (error) {

        } finally {
            setConnectorsLoading(false);
        }
    };

    const fetchIntegrations = async () => {
        try {
            const response = await api.get('/integrations', {
                params: { page: 1, page_size: 100 }
            });
            const integrationsData = response.data.items || [];
            setIntegrations(integrationsData);

            // Load events for all integrations
            const eventsMap = {};
            for (const integration of integrationsData) {
                try {
                    const eventsResponse = await api.get(`/integrations/${integration.id}/events`);
                    eventsMap[integration.id] = (eventsResponse.data || []).map(e => e.id);
                } catch (error) {
                    eventsMap[integration.id] = [];
                }
            }
            setIntegrationEvents(eventsMap);
        } catch (error) {
            toast.error('Erro ao carregar integrações');
        } finally {
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Nome é obrigatório';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            if (editingIntegration) {
                await api.put(`/integrations/${editingIntegration.id}`, formData);
            } else {
                await api.post('/integrations', formData);
            }
            toast.success(editingIntegration ? 'Integração atualizada' : 'Integração criada');
            setIsModalOpen(false);
            setFormData({ name: '', logo_url: '', supplier_id: null, system_id: null, documentation_links: [], connector_file: null, active: true });
            setEditingIntegration(null);
            fetchIntegrations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar integração');
        } finally {
        }
    };

    const handleDelete = async (id) => {
        toast.confirm(
            'Tem certeza?',
            async () => {
                try {
                    await api.delete(`/integrations/${id}`);
                    toast.success('Integração deletada');
                    fetchIntegrations();
                } catch (error) {
                    toast.error('Erro ao deletar integração');
                } finally {
                }
            },
            () => {
                // Cancel - do nothing
            }
        );
    };

    const openEditModal = (integration) => {
        setEditingIntegration(integration);
        setFormData({ name: integration.name, logo_url: integration.logo_url || '', supplier_id: integration.supplier_id || null, system_id: integration.system_id || null, documentation_links: integration.documentation_links || [], connector_file: integration.connector_file || null, active: integration.active });
        setConnectorSearch('');
        setConnectorDropdownOpen(false);
        setIsModalOpen(true);
    };

    const openNewModal = () => {
        setEditingIntegration(null);
        setFormData({ name: '', logo_url: '', supplier_id: null, system_id: null, documentation_links: [], connector_file: null, active: true });
        setFormErrors({});
        setNewLink({ title: '', url: '' });
        setLinkErrors({});
        setConnectorSearch('');
        setConnectorDropdownOpen(false);
        setIsModalOpen(true);
    };

    const addLink = () => {
        setLinkErrors({});

        if (!newLink.title.trim()) {
            setLinkErrors({ title: 'Título é obrigatório' });
            return;
        }

        if (!newLink.url.trim()) {
            setLinkErrors({ url: 'URL é obrigatória' });
            return;
        }

        if (!newLink.url.match(/^https?:\/\/.+/i)) {
            setLinkErrors({ url: 'URL deve começar com http:// ou https://' });
            return;
        }

        const currentLinks = Array.isArray(formData.documentation_links) ? formData.documentation_links : [];
        setFormData({
            ...formData,
            documentation_links: [...currentLinks, { title: newLink.title, url: newLink.url }]
        });
        setNewLink({ title: '', url: '' });
    };

    const removeLink = (index) => {
        setFormData({
            ...formData,
            documentation_links: formData.documentation_links.filter((_, i) => i !== index)
        });
    };


    const openEventsModal = async (integration) => {
        setSelectedIntegrationForEvents(integration);
        setEventSearchTerm('');

        // Carregar eventos associados dessa integração específica (sob demanda)
        // Os eventos globais já foram carregados no useEffect inicial via loadGlobalEvents()
        try {
            const eventsResponse = await api.get(`/integrations/${integration.id}/events`);
            const events = Array.isArray(eventsResponse.data) ? eventsResponse.data : (eventsResponse.data.items || []);
            setIntegrationEvents(prev => ({
                ...prev,
                [integration.id]: (events || []).map(e => typeof e === 'string' ? e : e.id)
            }));
        } catch (error) {

        }

        setIsEventsModalOpen(true);
    };

    const toggleIntegrationEvent = async (eventId) => {
        try {
            const token = localStorage.getItem('token');
            const integrationId = selectedIntegrationForEvents.id;
            const currentEvents = integrationEvents[integrationId] || [];
            const isChecked = currentEvents.includes(eventId);

            if (isChecked) {
                await associationAPI.disassociateEvent(integrationId, eventId, token);
                setIntegrationEvents({
                    ...integrationEvents,
                    [integrationId]: currentEvents.filter(id => id !== eventId)
                });
            } else {
                await associationAPI.associateEvent(integrationId, eventId, token);
                setIntegrationEvents({
                    ...integrationEvents,
                    [integrationId]: [...currentEvents, eventId]
                });
            }

        } catch (error) {
            toast.error(`Erro ao atualizar evento: ${error.message}`);
        }
    };

    const getIntegrationEvents = (integrationId) => {
        return integrationEvents[integrationId] || [];
    };

    // Group events by name
    const groupEventsByName = (events) => {
        return events.reduce((acc, event) => {
            const key = event.name;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(event);
            return acc;
        }, {});
    };

    // Toggle group expansion
    const toggleGroupExpansion = (groupName) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    // Toggle all events in a group
    const toggleGroupCheckbox = async (groupName, events) => {
        const integrationId = selectedIntegrationForEvents.id;
        const currentEvents = integrationEvents[integrationId] || [];
        const eventIds = events.map(e => e.id);
        const allChecked = eventIds.every(id => currentEvents.includes(id));

        try {
            if (allChecked) {
                // Uncheck all
                for (const id of eventIds) {
                    if (currentEvents.includes(id)) {
                        await associationAPI.disassociateEvent(integrationId, id, localStorage.getItem('token'));
                    }
                }
                setIntegrationEvents({
                    ...integrationEvents,
                    [integrationId]: currentEvents.filter(id => !eventIds.includes(id))
                });
            } else {
                // Check all
                for (const id of eventIds) {
                    if (!currentEvents.includes(id)) {
                        await associationAPI.associateEvent(integrationId, id, localStorage.getItem('token'));
                    }
                }
                setIntegrationEvents({
                    ...integrationEvents,
                    [integrationId]: [...currentEvents, ...eventIds.filter(id => !currentEvents.includes(id))]
                });
            }
        } catch (error) {
            toast.error(`Erro ao atualizar eventos do grupo: ${error.message}`);
        }
    };

    // Check if all events in a group are selected
    const isGroupFullyChecked = (events) => {
        const integrationId = selectedIntegrationForEvents.id;
        const currentEvents = integrationEvents[integrationId] || [];
        return events.length > 0 && events.every(event => currentEvents.includes(event.id));
    };

    // Check if some events in a group are selected
    const isGroupPartiallyChecked = (events) => {
        const integrationId = selectedIntegrationForEvents.id;
        const currentEvents = integrationEvents[integrationId] || [];
        return events.some(event => currentEvents.includes(event.id));
    };

    // Count selected events in a group
    const getSelectedCountInGroup = (events) => {
        const integrationId = selectedIntegrationForEvents.id;
        const currentEvents = integrationEvents[integrationId] || [];
        return events.filter(event => currentEvents.includes(event.id)).length;
    };

    const copyIntegrationIdToClipboard = (integrationId) => {
        navigator.clipboard.writeText(integrationId).then(() => {
            if (toast && toast.success) {
                toast.success('ID da integração copiado para área de transferência');
            }
        }).catch(() => {
            if (toast && toast.error) {
                toast.error('Erro ao copiar ID da integração');
            }
        });
    };

    // Funções para modal de configurações/parâmetros
    const openConfigModal = async (integration) => {
        setSelectedIntegrationForConfig(integration);
        setConfigLoading(true);
        setIsConfigModalOpen(true);

        try {
            const response = await api.get(`/integrations/${integration.id}/parameters`);
            setConfigParameters(response.data.items || []);
        } catch (error) {
            setConfigParameters([]);
        } finally {
            setConfigLoading(false);
        }
    };

    const closeConfigModal = () => {
        setIsConfigModalOpen(false);
        setSelectedIntegrationForConfig(null);
        setConfigParameters([]);
    };

    const addConfigParameter = () => {
        setConfigParameters([...configParameters, {
            id: `temp-${Date.now()}`,
            code: '',
            title: '',
            param_type: 'string',
            required: false,
            isNew: true
        }]);
    };

    const removeConfigParameter = (index) => {
        setConfigParameters(configParameters.filter((_, i) => i !== index));
    };

    const updateConfigParameter = (index, field, value) => {
        const updated = [...configParameters];
        updated[index] = { ...updated[index], [field]: value };
        setConfigParameters(updated);
    };

    const saveConfigParameters = async () => {
        // Validação
        const hasErrors = configParameters.some(p => !p.code.trim() || !p.title.trim());
        if (hasErrors) {
            toast.error('Preencha o código e título de todos os parâmetros');
            return;
        }

        // Verifica códigos duplicados
        const codes = configParameters.map(p => p.code.trim().toLowerCase());
        const hasDuplicates = codes.length !== new Set(codes).size;
        if (hasDuplicates) {
            toast.error('Existem códigos duplicados');
            return;
        }

        setConfigSaving(true);
        try {
            const response = await api.post(`/integrations/${selectedIntegrationForConfig.id}/parameters/bulk`, {
                parameters: configParameters.map(p => ({
                    code: p.code.trim(),
                    title: p.title.trim(),
                    param_type: p.param_type,
                    required: p.required
                }))
            });
            setConfigParameters(response.data.items || []);
            toast.success('Configurações salvas com sucesso');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
        } finally {
            setConfigSaving(false);
        }
    };

    const zapIcon = <span className="material-icons mx-auto mb-4 opacity-50" style={{fontSize: '48px'}}>bolt</span>;
    const plusIcon = <span className="material-icons sm">add</span>;
    const editIcon = <span className="material-icons sm">edit</span>;
    const deleteIcon = <span className="material-icons sm">delete</span>;
    const checkIcon = <span className="material-icons sm">check_circle</span>;
    const xIcon = <span className="material-icons sm">cancel</span>;
    const saveIcon = <span className="material-icons sm">save</span>;
    const closeIcon = <span className="material-icons sm">close</span>;
    const copyIcon = <span className="material-icons" style={{fontSize: '14px'}}>content_copy</span>;

    return (
        <>
        <div className="flex-1 overflow-auto">
            <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <span className="material-icons text-purple-500 dark:text-purple-400" style={{fontSize: '40px'}}>integration_instructions</span>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Integrações</h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Conectar e gerenciar integrações</p>
                        </div>
                    </div>
                    <Button onClick={openNewModal} variant="primary">{plusIcon} Nova Integração</Button>
                </div>

                {!webhookLoading && defaultWebhookUrl && (
                    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setWebhookAccordionOpen(!webhookAccordionOpen)}
                            className="w-full flex items-center justify-between gap-3 p-4 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition text-left"
                        >
                            <div className="flex items-center gap-3 flex-1">
                                <span className="material-icons text-blue-600 dark:text-blue-400 flex-shrink-0" style={{fontSize: '20px'}}>info</span>
                                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                    IMPORTANTE
                                </p>
                            </div>
                            <span className="material-icons text-blue-600 dark:text-blue-400 flex-shrink-0 transition" style={{fontSize: '20px', transform: webhookAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}>
                                expand_more
                            </span>
                        </button>

                        {webhookAccordionOpen && (
                            <div className="border-t border-blue-200 dark:border-blue-800 p-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-xs text-blue-800 dark:text-blue-200">
                                        Integrações sem conector encaminham eventos para
                                    </p>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-mono whitespace-nowrap">
                                        {defaultWebhookUrl}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <span className="material-icons absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" style={{fontSize: '20px'}}>search</span>
                        <input
                            type="text"
                            placeholder="Pesquisar por ID, nome, fornecedor ou sistema..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                    {integrations.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center gap-2">
                                {zapIcon}
                                <p>Nenhuma integração cadastrada</p>
                            </div>
                        </div>
                    ) : filteredIntegrations.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center gap-2">
                                <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '40px'}}>search_off</span>
                                <p>Nenhuma integração encontrada para "{searchTerm}"</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {(() => {
                                return paginatedSupplierKeys.map((supplierId) => {
                                    const supplier = groupedIntegrationsBySupplier[supplierId];
                                    const isExpanded = expandedIntegrationSuppliers[supplierId];

                                    return (
                                        <div key={supplierId} className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0`}>
                                            {/* Acordeão Header */}
                                            <button
                                                type="button"
                                                onClick={() => toggleIntegrationSupplierExpansion(supplierId)}
                                                className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-left"
                                            >
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <span className="material-icons text-gray-400 dark:text-gray-500 transition flex-shrink-0" style={{fontSize: '18px'}}>
                                                        {isExpanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                                        {supplier.logo_url ? (
                                                            <img src={supplier.logo_url} alt={supplier.name} className="w-full h-full object-contain p-1 rounded" />
                                                        ) : (
                                                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '16px'}}>image</span>
                                                        )}
                                                    </div>
                                                    <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{supplier.name}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {(() => {
                                                        const missingConnectors = supplier.integrations.filter(int => !int.connector_file).length;
                                                        return missingConnectors > 0 && (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                                                <span className="material-icons" style={{fontSize: '14px'}}>warning</span>
                                                                {missingConnectors} conector{missingConnectors !== 1 ? 'es' : ''} ausente{missingConnectors !== 1 ? 's' : ''}
                                                            </span>
                                                        );
                                                    })()}
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                        {supplier.integrations.length} integração{supplier.integrations.length !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </button>

                                            {/* Conteúdo expandido - Tabela */}
                                            {isExpanded && (
                                                <div className="bg-white dark:bg-gray-800/50 overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                                                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Nome</th>
                                                                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">ID</th>
                                                                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Sistema</th>
                                                                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Eventos</th>
                                                                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Conector</th>
                                                                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Status</th>
                                                                <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-300">Ações</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                            {supplier.integrations.map((integration) => (
                                                                <tr key={integration.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                                                                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium max-w-xs truncate">{integration.name}</td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-mono text-xs text-gray-600 dark:text-gray-300 truncate">{integration.id.slice(0, 8)}...</span>
                                                                            <button
                                                                                onClick={() => copyIntegrationIdToClipboard(integration.id)}
                                                                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition flex-shrink-0"
                                                                                title="Copiar ID"
                                                                            >
                                                                                {copyIcon}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {(() => {
                                                                            const supplier = suppliers.find(s => s.id === integration.supplier_id);
                                                                            const system = supplier?.systems?.find(sys => sys.id === integration.system_id);
                                                                            return system ? (
                                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                                    {system.name || system.description}
                                                                                </span>
                                                                            ) : null;
                                                                        })()}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {getIntegrationEvents(integration.id).length > 0 && (
                                                                            <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                                                                                {getIntegrationEvents(integration.id).length}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {integration.connector_file ? (
                                                                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 truncate max-w-xs">
                                                                                <span className="material-icons flex-shrink-0" style={{fontSize: '14px'}}>check_circle</span>
                                                                                <span className="truncate">{integration.connector_file}</span>
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                                                                                <span className="material-icons flex-shrink-0" style={{fontSize: '14px'}}>warning</span>
                                                                                Ausente
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        {integration.active ? (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                                                                                <span className="w-1.5 h-1.5 bg-green-600 dark:bg-green-400 rounded-full"></span>
                                                                                Ativo
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                                <span className="w-1.5 h-1.5 bg-gray-600 dark:bg-gray-400 rounded-full"></span>
                                                                                Inativo
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex gap-2 justify-center flex-shrink-0">
                                                                            <button
                                                                                onClick={() => openConfigModal(integration)}
                                                                                className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                                                title="Configurações"
                                                                            >
                                                                                <span className="material-icons sm">settings</span>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => openEventsModal(integration)}
                                                                                className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                                                title="Eventos"
                                                                            >
                                                                                <span className="material-icons sm">schedule</span>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => openEditModal(integration)}
                                                                                className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                                                title="Editar"
                                                                            >
                                                                                {editIcon}
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleDelete(integration.id)}
                                                                                className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-red-500 hover:border-red-600 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 dark:border-red-400 transition"
                                                                                title="Remover"
                                                                            >
                                                                                {deleteIcon}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                            <Pagination
                                currentPage={page}
                                totalPages={totalSupplierPages}
                                totalItems={supplierKeys.length}
                                itemsPerPage={suppliersPerPage}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>

        <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setFormErrors({}); setLogoValid(null); }} title={editingIntegration ? 'Editar Integração' : 'Nova Integração'}>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {editingIntegration && (
                        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">ID da Integração</p>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{editingIntegration.id}</span>
                                <button
                                    type="button"
                                    onClick={() => copyIntegrationIdToClipboard(editingIntegration.id)}
                                    className="flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                                    title="Copiar ID da integração"
                                >
                                    {copyIcon}
                                </button>
                            </div>
                        </div>
                    )}
                    <Input label="Nome" placeholder="Nome da integração" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} error={formErrors.name} />

                    {/* Supplier Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Fornecedor
                        </label>
                        <select
                            value={formData.supplier_id || ''}
                            onChange={(e) => {
                                const supplierId = e.target.value;
                                setFormData({ ...formData, supplier_id: supplierId || null, system_id: null });
                            }}
                            
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400"
                        >
                            <option value="">Selecione um fornecedor...</option>
                            {[...suppliers].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map(supplier => (
                                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* System Selection */}
                    {formData.supplier_id && (() => {
                        const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);
                        const supplierSystems = selectedSupplier?.systems || [];
                        return (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Sistema
                                </label>
                                <select
                                    value={formData.system_id || ''}
                                    onChange={(e) => {
                                        setFormData({ ...formData, system_id: e.target.value || null });
                                    }}
                                    disabled={supplierSystems.length === 0}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400"
                                >
                                    <option value="">
                                        {supplierSystems.length === 0 ? 'Nenhum sistema disponível' : 'Selecione um sistema...'}
                                    </option>
                                    {[...supplierSystems].sort((a, b) => (a.name || a.description).toLowerCase().localeCompare((b.name || b.description).toLowerCase())).map(system => (
                                        <option key={system.id} value={system.id}>{system.name || system.description}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })()}

                    {/* Connector File Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Conector
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Arquivo Python responsável pelo processamento desta integração
                        </p>

                        {formData.connector_file ? (
                            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                                <span className="material-icons text-blue-500 dark:text-blue-400" style={{fontSize: '20px'}}>code</span>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {(() => { const c = connectors.find(f => f.filename === formData.connector_file); return c ? c.display_name : formData.connector_file; })()}
                                    </span>
                                    <span className="block text-xs text-gray-400 dark:text-gray-500 truncate">{formData.connector_file}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, connector_file: null })}

                                    className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition"
                                    title="Remover conector"
                                >
                                    <span className="material-icons" style={{fontSize: '18px'}}>close</span>
                                </button>
                            </div>
                        ) : (
                            <div className="relative" ref={connectorDropdownRef}>
                                <div
                                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition"
                                    onClick={() => { setConnectorDropdownOpen(!connectorDropdownOpen); setConnectorSearch(''); }}
                                >
                                    <span className="material-icons text-gray-400" style={{fontSize: '20px'}}>search</span>
                                    <input
                                        type="text"
                                        placeholder="Buscar conector..."
                                        value={connectorSearch}
                                        onChange={(e) => { e.stopPropagation(); setConnectorSearch(e.target.value); setConnectorDropdownOpen(true); }}
                                        onFocus={() => setConnectorDropdownOpen(true)}
                                        className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400"
                                       
                                    />
                                    <span className="material-icons text-gray-400" style={{fontSize: '18px'}}>
                                        {connectorDropdownOpen ? 'expand_less' : 'expand_more'}
                                    </span>
                                </div>

                                {connectorDropdownOpen && (
                                    <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {connectorsLoading ? (
                                            <div className="flex items-center justify-center gap-2 p-4 text-gray-500 dark:text-gray-400">
                                                <span className="material-icons animate-spin" style={{fontSize: '18px'}}>refresh</span>
                                                <span className="text-sm">Carregando...</span>
                                            </div>
                                        ) : connectors.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center gap-2 p-6 text-gray-400 dark:text-gray-500">
                                                <span className="material-icons" style={{fontSize: '32px'}}>folder_off</span>
                                                <span className="text-sm">Nenhum conector aprovado</span>
                                                <span className="text-xs text-gray-400 dark:text-gray-500">Envie e aprove conectores no Dev Portal</span>
                                            </div>
                                        ) : (() => {
                                            const filtered = connectors.filter(c =>
                                                c.display_name.toLowerCase().includes(connectorSearch.toLowerCase()) ||
                                                c.filename.toLowerCase().includes(connectorSearch.toLowerCase())
                                            );
                                            return filtered.length === 0 ? (
                                                <div className="flex items-center justify-center gap-2 p-4 text-gray-400 dark:text-gray-500">
                                                    <span className="material-icons" style={{fontSize: '18px'}}>search_off</span>
                                                    <span className="text-sm">Nenhum resultado para "{connectorSearch}"</span>
                                                </div>
                                            ) : (
                                                filtered.map(connector => (
                                                    <div
                                                        key={connector.filename}
                                                        onClick={() => {
                                                            setFormData({ ...formData, connector_file: connector.filename });
                                                            setConnectorDropdownOpen(false);
                                                            setConnectorSearch('');
                                                        }}
                                                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition"
                                                    >
                                                        <span className="material-icons text-blue-500 dark:text-blue-400" style={{fontSize: '20px'}}>code</span>
                                                        <div className="flex-1 min-w-0">
                                                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{connector.display_name}</span>
                                                            <span className="block text-xs text-gray-400 dark:text-gray-500 truncate">{connector.filename}</span>
                                                        </div>
                                                        {connector.description && (
                                                            <span className="material-icons text-gray-300 dark:text-gray-600" style={{fontSize: '16px'}} title={connector.description}>info</span>
                                                        )}
                                                    </div>
                                                ))
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Documentation Links Section */}
                    <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Links de Documentação
                        </label>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                            Adicione links para documentação, guias ou arquivos que apoiem o entendimento da integração
                        </p>

                        {/* Add Link Form */}
                        <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                            <Input
                                label="Título do Link"
                                placeholder="Ex: Documentação da API"
                                value={newLink.title}
                                onChange={(e) => { setNewLink({ ...newLink, title: e.target.value }); setLinkErrors({}); }}
                                error={linkErrors.title}
                               
                            />
                            <Input
                                label="URL"
                                placeholder="https://example.com/docs"
                                value={newLink.url}
                                onChange={(e) => { setNewLink({ ...newLink, url: e.target.value }); setLinkErrors({}); }}
                                error={linkErrors.url}
                               
                            />
                            <button
                                type="button"
                                onClick={addLink}
                               
                                className="w-full px-3 py-2 text-sm font-medium bg-white dark:bg-gray-600 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 rounded transition disabled:opacity-50"
                            >
                                <span className="material-icons sm inline mr-2" style={{fontSize: '16px', verticalAlign: 'middle'}}>add_link</span>
                                Adicionar Link
                            </button>
                        </div>

                        {/* Links List */}
                        {formData.documentation_links && formData.documentation_links.length > 0 && (
                            <div className="space-y-2">
                                {formData.documentation_links.map((link, index) => (
                                    <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{link.title}</p>
                                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block">
                                                {link.url}
                                            </a>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeLink(index)}
                                           
                                            className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition flex-shrink-0"
                                            title="Remover"
                                        >
                                            <span className="material-icons sm">delete</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} className="w-4 h-4 rounded" />
                        <span className="text-sm">Ativa</span>
                    </label>
                    <div className="flex gap-2 pt-4">
                        <Button type="submit" variant="primary" className="flex-1">{editingIntegration ? editIcon : plusIcon} {editingIntegration ? 'Atualizar' : 'Criar'}</Button>
                        <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} className="flex-1">{closeIcon} Cancelar</Button>
                    </div>
                </form>
            </Modal>

            {isEventsModalOpen && selectedIntegrationForEvents && (
                <Fragment>
                    <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setIsEventsModalOpen(false)}></div>
                    <div className="fixed right-0 top-0 h-screen w-full max-w-md bg-white dark:bg-gray-800 shadow-lg z-40 flex flex-col">
                        {/* Header fixo */}
                        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Eventos</h2>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                            {localEventsData.length}
                                        </span>
                                    </div>
                                    <div className="flex items-start gap-2 mt-2">
                                        {selectedIntegrationForEvents.logo_url ? (
                                            <img src={selectedIntegrationForEvents.logo_url} alt={selectedIntegrationForEvents.name} className="h-6 w-6 object-cover rounded flex-shrink-0 mt-0.5" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                        ) : null}
                                        <div style={{ display: selectedIntegrationForEvents.logo_url ? 'none' : 'flex' }} className="h-6 w-6 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '16px'}}>image</span>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedIntegrationForEvents.name}</p>
                                            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{selectedIntegrationForEvents.id}</span>
                                                <button
                                                    onClick={() => copyIntegrationIdToClipboard(selectedIntegrationForEvents.id)}
                                                    className="flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
                                                    title="Copiar ID da integração"
                                                >
                                                    {copyIcon}
                                                </button>
                                            </div>
                                            <div className="mt-2 space-y-1">
                                                {selectedIntegrationForEvents.supplier_id && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                                        <span className="text-gray-500 dark:text-gray-500">Fornecedor:</span> {suppliers.find(s => s.id === selectedIntegrationForEvents.supplier_id)?.name || '-'}
                                                    </p>
                                                )}
                                                {selectedIntegrationForEvents.system_id && (() => {
                                                    const supplier = suppliers.find(s => s.id === selectedIntegrationForEvents.supplier_id);
                                                    const system = supplier?.systems?.find(sys => sys.id === selectedIntegrationForEvents.system_id);
                                                    return (
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                                            <span className="text-gray-500 dark:text-gray-500">Sistema:</span> {system?.name || system?.description || '-'}
                                                        </p>
                                                    );
                                                })()}
                                                {selectedIntegrationForEvents.system?.system_type && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                                        <span className="text-gray-500 dark:text-gray-500">Tipo:</span> {selectedIntegrationForEvents.system.system_type.name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsEventsModalOpen(false)}
                                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition flex-shrink-0 -mt-1"
                                >
                                    <span className="material-icons sm">close</span>
                                </button>
                            </div>
                        </div>

                        {/* Campo de filtro fixo */}
                        {localEventsData.length > 0 && (
                            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="relative">
                                    <span className="material-icons absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" style={{fontSize: '20px'}}>search</span>
                                    <input
                                        type="text"
                                        placeholder="Filtrar eventos..."
                                        value={eventSearchTerm}
                                        onChange={(e) => setEventSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Área com scroll */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {localEventsData.length === 0 ? (
                                <div className="text-center py-8">
                                    <span className="material-icons mx-auto mb-3 text-gray-400 dark:text-gray-600" style={{fontSize: '48px'}}>event_note</span>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Nenhum evento disponível
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                        Crie um na página de Configurações
                                    </p>
                                </div>
                            ) : (() => {
                                const filteredEvents = localEventsData.filter(event => {
                                    const searchLower = eventSearchTerm.toLowerCase();
                                    return (
                                        event.name.toLowerCase().includes(searchLower) ||
                                        event.type.toLowerCase().includes(searchLower) ||
                                        event.id.toLowerCase().includes(searchLower)
                                    );
                                });

                                return filteredEvents.length === 0 ? (
                                    <div className="text-center py-8">
                                        <span className="material-icons mx-auto mb-3 text-gray-400 dark:text-gray-600" style={{fontSize: '48px'}}>search_off</span>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Nenhum evento encontrado
                                        </p>
                                    </div>
                                ) : (() => {
                                    const groupedEvents = groupEventsByName(filteredEvents);
                                    const groupNames = Object.keys(groupedEvents).sort();

                                    return (
                                        <div className="space-y-3">
                                            {groupNames.map(groupName => {
                                                const events = groupedEvents[groupName];
                                                const isExpanded = expandedGroups[groupName];
                                                const isFullyChecked = isGroupFullyChecked(events);
                                                const isPartiallyChecked = isGroupPartiallyChecked(events);

                                                return (
                                                    <div key={groupName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                        {/* Acordeão Header */}
                                                        <div className="bg-gray-100 dark:bg-gray-700/50">
                                                            <div className="flex items-start gap-3 px-3 py-3">
                                                                {/* Checkbox do grupo */}
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isFullyChecked}
                                                                    onChange={() => toggleGroupCheckbox(groupName, events)}
                                                                    className="w-4 h-4 rounded border-gray-300 text-blue-500 cursor-pointer flex-shrink-0 mt-0.5"
                                                                />

                                                                {/* Botão expandir/recolher e nome */}
                                                                <button
                                                                    onClick={() => toggleGroupExpansion(groupName)}
                                                                    className="flex-1 min-w-0 text-left"
                                                                >
                                                                    <div className="flex items-start gap-2">
                                                                        <span className="material-icons text-gray-400 dark:text-gray-500 transition flex-shrink-0" style={{fontSize: '18px'}}>
                                                                            {isExpanded ? 'expand_less' : 'expand_more'}
                                                                        </span>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{groupName}</p>
                                                                            <div className="mt-1 flex items-center gap-2">
                                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                                                    {events.length} evento{events.length !== 1 ? 's' : ''}
                                                                                </span>
                                                                                {getSelectedCountInGroup(events) > 0 && (
                                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                                                        {getSelectedCountInGroup(events)} selecionado{getSelectedCountInGroup(events) !== 1 ? 's' : ''}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            </div>

                                                            {/* Conteúdo expandido */}
                                                            {isExpanded && (
                                                                <div className="border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50">
                                                                    {events.map((event, index) => {
                                                                        const integrationEventIds = getIntegrationEvents(selectedIntegrationForEvents.id);
                                                                        const isChecked = integrationEventIds.includes(event.id);

                                                                        return (
                                                                            <div
                                                                                key={event.id}
                                                                                className={`flex items-start gap-3 px-6 py-2 ${
                                                                                    index !== events.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                                                                                }`}
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isChecked}
                                                                                    onChange={() => toggleIntegrationEvent(event.id)}
                                                                                    className="w-4 h-4 rounded border-gray-300 text-blue-500 cursor-pointer flex-shrink-0 mt-0.5"
                                                                                />
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                                                                                            isChecked
                                                                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                                                                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                                                                        }`}>
                                                                                            {event.type}
                                                                                        </span>
                                                                                    </div>
                                                                                    <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-1">
                                                                                        {event.id}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })();
                            })()}
                        </div>

                        {/* Footer fixo */}
                        {localEventsData.length > 0 && (
                            <div className="flex-shrink-0 p-6 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {(() => {
                                        const eventCount = getIntegrationEvents(selectedIntegrationForEvents.id).length;
                                        return `${eventCount} evento${eventCount !== 1 ? 's' : ''} selecionado${eventCount !== 1 ? 's' : ''}`;
                                    })()}
                                </p>
                            </div>
                        )}
                    </div>
                </Fragment>
            )}

            {/* Modal de Configurações/Parâmetros */}
            {isConfigModalOpen && selectedIntegrationForConfig && (
                <Fragment>
                    <div className="fixed inset-0 bg-black/50 z-30" onClick={closeConfigModal}></div>
                    <div className="fixed right-0 top-0 h-screen w-full max-w-lg bg-white dark:bg-gray-800 shadow-xl z-40 flex flex-col">
                        {/* Header fixo */}
                        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="material-icons text-amber-500 dark:text-amber-400" style={{fontSize: '28px'}}>settings</span>
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Configurações</h2>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{selectedIntegrationForConfig.name}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeConfigModal}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                >
                                    <span className="material-icons">close</span>
                                </button>
                            </div>
                        </div>

                        {/* Conteúdo scrollável */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {configLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Defina os parâmetros de configuração desta integração
                                        </p>
                                        <button
                                            onClick={addConfigParameter}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded transition"
                                        >
                                            <span className="material-icons" style={{fontSize: '18px'}}>add</span>
                                            Adicionar
                                        </button>
                                    </div>

                                    {configParameters.length === 0 ? (
                                        <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                                            <span className="material-icons text-gray-400 dark:text-gray-500 mb-3" style={{fontSize: '48px'}}>tune</span>
                                            <p className="text-gray-500 dark:text-gray-400">Nenhum parâmetro configurado</p>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Clique em "Adicionar" para criar um parâmetro</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {configParameters.map((param, index) => (
                                                <div key={param.id || index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-xs font-medium">
                                                            {index + 1}
                                                        </span>
                                                        <button
                                                            onClick={() => removeConfigParameter(index)}
                                                            className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition"
                                                            title="Remover parâmetro"
                                                        >
                                                            <span className="material-icons" style={{fontSize: '20px'}}>delete</span>
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                                Código *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={param.code}
                                                                onChange={(e) => updateConfigParameter(index, 'code', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                                                                placeholder="ex: api_key"
                                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                                Tipo
                                                            </label>
                                                            <select
                                                                value={param.param_type}
                                                                onChange={(e) => updateConfigParameter(index, 'param_type', e.target.value)}
                                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                            >
                                                                {parameterTypes.map(type => (
                                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="mt-3">
                                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                            Título *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={param.title}
                                                            onChange={(e) => updateConfigParameter(index, 'title', e.target.value)}
                                                            placeholder="ex: Chave da API"
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                        />
                                                    </div>

                                                    <div className="mt-3">
                                                        <label className="flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={param.required}
                                                                onChange={(e) => updateConfigParameter(index, 'required', e.target.checked)}
                                                                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                                                            />
                                                            <span className="text-sm text-gray-700 dark:text-gray-300">Obrigatório</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer fixo */}
                        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                            <div className="flex gap-3">
                                <button
                                    onClick={saveConfigParameters}
                                    disabled={configSaving || configLoading}
                                    className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-400 text-white font-medium rounded-lg transition flex items-center justify-center gap-2"
                                >
                                    {configSaving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-icons" style={{fontSize: '20px'}}>save</span>
                                            Salvar
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={closeConfigModal}
                                    disabled={configSaving}
                                    className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 font-medium rounded-lg transition flex items-center justify-center gap-2"
                                >
                                    <span className="material-icons" style={{fontSize: '20px'}}>close</span>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </Fragment>
            )}
        </>
    );
};

// ============ Integration Mapping Page ============

export default IntegrationsPage
